import React from 'react'
import { UnassignedParticipantContainer } from '../UnassignedParticipantContainer/UnassignedParticipantContainer'
import { TeamContainer } from '../TeamContainer/TeamContainer'
import { Participant, Team } from '../../types'

export type ParticipantsContainerMode = 'creation' | 'edit' | 'preview'

export interface ParticipantsContainerProps {
  participants: Participant[]
  teams: Team[]
  mode: ParticipantsContainerMode
  onAddTeam: () => void
  onRemoveTeam: (teamId: string) => void
  onRemoveParticipant: (participantId: string) => void
  onUpdateTeamLanguage?: (teamId: string, language: string) => void
  onApplyLanguageToAll?: (language: string) => void
  onDropParticipant?: (participantId: string) => void
  onDropToTeam?: (participantId: string, teamId: string) => void
  onAutoAssign?: () => void
  unassignedCount?: number
  hasUnassignedParticipants?: boolean
  hasValidationErrors?: boolean
  hasMissingTeamLanguages?: boolean
  className?: string
}

export const ParticipantsContainer: React.FC<ParticipantsContainerProps> = ({
  participants,
  teams,
  mode,
  onAddTeam,
  onRemoveTeam,
  onRemoveParticipant,
  onUpdateTeamLanguage,
  onApplyLanguageToAll,
  onDropParticipant,
  onDropToTeam,
  onAutoAssign,
  unassignedCount = 0,
  hasUnassignedParticipants: _hasUnassignedParticipants = false,
  hasValidationErrors = false,
  hasMissingTeamLanguages = false,
  className = ''
}) => {
  // Debug logging

  // Separate unassigned participants from team participants with defensive checks
  const teamParticipantIds = new Set(
    teams.flatMap((team) =>
      Array.isArray(team.participants) ? team.participants.map((p) => p?.id).filter(Boolean) : []
    )
  )
  const unassignedParticipants = participants.filter((p: Participant) => p && p.id && !teamParticipantIds.has(p.id))

  // Filter teams for preview mode (remove empty teams) with defensive checks
  const displayTeams =
    mode === 'preview'
      ? teams.filter((team) => Array.isArray(team.participants) && team.participants.length > 0)
      : teams

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Unassigned participants - only show in creation and edit modes */}
      {mode !== 'preview' && (
        <UnassignedParticipantContainer
          participants={unassignedParticipants}
          onRemoveParticipant={onRemoveParticipant}
          onDropParticipant={onDropParticipant}
          hasValidationErrors={hasValidationErrors}
          mode={mode}
        />
      )}

      {/* Teams */}
      <TeamContainer
        teams={displayTeams}
        onAddTeam={onAddTeam}
        onRemoveTeam={onRemoveTeam}
        onRemoveParticipant={onRemoveParticipant}
        onUpdateTeamLanguage={onUpdateTeamLanguage}
        onApplyLanguageToAll={onApplyLanguageToAll}
        onDropParticipant={onDropToTeam}
        onAutoAssign={onAutoAssign}
        unassignedCount={unassignedCount}
        mode={mode}
        hasValidationErrors={hasValidationErrors}
        hasMissingTeamLanguages={hasMissingTeamLanguages}
      />
    </div>
  )
}
